import { cn } from "../../lib/utils";

export type ChipItem<T extends string> = {
  value: T;
  label: string;
};

/**
 * A row of mutually exclusive chips.
 *
 * Exists because nothing selectable did: `Tag` is a display `<span>` with no
 * `onClick`, and `SegmentedControl` is a sliding pill sized for two to five wide
 * segments — wrong for the two shapes here, which are a direction refinement
 * appearing under a low score ("too long" / "too short") and a seven-way tool
 * picker that wraps onto several lines.
 *
 * Generic over the value union so call sites stay exhaustively typed, like
 * `SegmentedControl`. Single-select: both callers are.
 *
 * `value` is nullable because "nothing chosen yet" is a real state — a direction
 * row appears unanswered, and forcing a default would put a direction on a
 * criterion the clinician has not qualified.
 */
export function ChipRow<T extends string>({
  value,
  onChange,
  items,
  label,
  className,
}: {
  value: T | null;
  onChange: (value: T) => void;
  items: ChipItem<T>[];
  // Names the group for assistive tech. The visible caption, when there is one,
  // is the caller's — this row is only the chips.
  label: string;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn("flex flex-wrap items-center gap-2", className)}
    >
      {items.map((item) => {
        const selected = value === item.value;
        return (
          <button
            key={item.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => {
              onChange(item.value);
            }}
            className={cn(
              // min-h rather than a fixed height: a long tool name wraps rather
              // than overflowing, and the target stays tappable either way.
              "min-h-9 rounded-full px-3 py-1.5",
              "font-dm-sans text-ui-note font-medium",
              "cursor-pointer transition-colors",
              "focus-visible:outline-none focus-visible:shadow-focus-ring",
              selected
                ? "bg-ds-mint-100 text-ds-mint-800"
                : "border border-ds-slate-200 bg-white text-ds-slate-600 hover:bg-ds-slate-100"
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
